import type { FC } from 'react';

import React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router';

export type Vertical = 'buy' | 'rent';

interface VerticalSwitchProps {
  /** The vertical this screen shows. */
  active: Vertical;
  /** Where "Buy" leads: `/buy`, or the dealer's page. */
  buyTo: string;
  /** Where "Rent" leads: `/rent`, or the dealer's page with `?vertical=rent`. */
  rentTo: string;
}

/**
 * Buy / Rent on the first screen of a catalog (storefront-catalog spec §4.2): two links, so each
 * vertical has an address, a Tab stop and a ring, and a finger-sized target on a phone.
 *
 * `active` decides which one is current, not the address: on the dealer's page both links share a
 * path and differ only in `?vertical=rent`, which `NavLink` does not compare — left to itself it
 * would mark both. The other segment says `aria-current="false"`, which is "not the current one".
 */
export const VerticalSwitch: FC<VerticalSwitchProps> = ({ active, buyTo, rentTo }) => (
  <Segments>
    <Segment to={buyTo} end aria-current={active === 'buy' ? 'page' : 'false'} $active={active === 'buy'}>
      Buy
    </Segment>
    <Segment to={rentTo} end aria-current={active === 'rent' ? 'page' : 'false'} $active={active === 'rent'}>
      Rent
    </Segment>
  </Segments>
);

const Segments = styled.div`
  display: inline-flex;
  align-self: flex-start;
  gap: ${({ theme }) => theme.space.xxs}px;
  padding: ${({ theme }) => theme.space.xxs}px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.round}px;
  background: ${({ theme }) => theme.colors.surfaceRaised};
`;

/* The current vertical is the accent with black text on it — the one lime block on the screen;
   the other stays a raised surface with secondary text. */
const Segment = styled(NavLink)<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${({ theme }) => theme.control.touchTarget}px;
  min-width: ${({ theme }) => theme.control.touchTarget}px;
  padding: 0 ${({ theme }) => theme.space.lg}px;
  border-radius: ${({ theme }) => theme.borderRadius.round}px;
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.large.fontSize}px;
  line-height: ${({ theme }) => theme.typography.large.lineHeight}px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-decoration: none;
  background: ${({ theme, $active }) => ($active ? theme.colors.accent : theme.colors.surfaceRaised)};
  color: ${({ theme, $active }) => ($active ? theme.colors.accentInk : theme.colors.lightWhite)};

  &:focus-visible {
    outline: var(--gac-control-focus-ring) solid var(--gac-text-primary);
    outline-offset: 2px;
  }
`;
